import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "../lib/logger";

interface SEOHeadProps {
  poll: {
    id: string;
    slug?: string;
    title: string;
    type: string;
    participantCount?: number;
    deadline?: string;
    dates?: string[];
    category?: string;
  };
  baseUrl?: string;
}

// Bug hunt 2026-04-23 #7: les réseaux sociaux ne ré-exécutent pas le HTML,
// mais ils l'affichent littéralement. On retire les balises HTML du titre avant
// de l'écrire dans <title> / og:title / twitter:title pour éviter des previews
// avec "<script>alert(1)</script>" visible.
const stripHtml = (s: string): string => {
  let stripped: string;
  if (typeof document === "undefined") {
    stripped = s.replace(/<[^>]*>/g, "");
  } else {
    const doc = new DOMParser().parseFromString(s, "text/html");
    stripped = (doc.body.textContent || "").trim();
  }
  // Decode HTML entities (e.g., &#x27; → ', &amp; → &, etc.)
  // Needed because poll.title may contain escaped entities that shouldn't appear literal in OG tags
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = stripped;
    return textarea.value;
  }
  return stripped;
};

export const SEOHead: React.FC<SEOHeadProps> = ({ poll, baseUrl }) => {
  const { t, i18n } = useTranslation("polls");
  useEffect(() => {
    try {
      const safeTitle = stripHtml(poll.title);
      // Canonical URL should match the actual public route the crawler sees.
      // Use window.location.origin in the browser; the Vercel Edge middleware
      // handles crawler-side SSR, so this runs for humans only.
      const origin =
        baseUrl ||
        (typeof window !== "undefined"
          ? window.location.origin
          : "https://doodates.com");
      const pathname =
        typeof window !== "undefined"
          ? window.location.pathname
          : `/poll/${poll.slug || poll.id}/vote`;
      const canonicalUrl = `${origin}${pathname}`;

      // Image OG : même fonction Vercel /api/og que le rendu côté robots
      // (middleware.ts), mêmes paramètres. L'ancienne edge function Supabase
      // og-image ne répondait plus (retirée le 02/10/2026).
      const ogImageUrl = `${origin}/api/og?${new URLSearchParams({
        title: safeTitle,
        participantCount: (poll.participantCount || 0).toString(),
        dates: (poll.dates || []).slice(0, 3).join(","),
        ...(poll.category ? { category: poll.category } : {}),
      }).toString()}`;

      // Update or create meta tags
      const updateMetaTag = (property: string, content: string) => {
        let tag =
          document.querySelector(`meta[property="${property}"]`) ||
          document.querySelector(`meta[name="${property}"]`);

        if (!tag) {
          tag = document.createElement("meta");
          // Open Graph utilise `property=`, les Twitter Cards `name=` (spec
          // X) : `twitter:card` posé en `property` était ignoré.
          tag.setAttribute(
            property.startsWith("og:") ? "property" : "name",
            property,
          );
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      };

      // Update title
      document.title = `${safeTitle} — DooDates`;

      // Update description
      const description = t("seo_vote_description", {
        count: poll.participantCount || 0,
      });

      // Basic meta tags
      updateMetaTag("description", description);
      updateMetaTag("author", "DooDates");

      // Open Graph tags
      updateMetaTag("og:type", "website");
      updateMetaTag("og:url", canonicalUrl);
      updateMetaTag("og:title", safeTitle);
      updateMetaTag("og:description", description);
      updateMetaTag("og:image", ogImageUrl);
      updateMetaTag("og:image:width", "1200");
      updateMetaTag("og:image:height", "630");
      updateMetaTag("og:site_name", "DooDates");

      // Twitter Card tags
      updateMetaTag("twitter:card", "summary_large_image");
      updateMetaTag("twitter:url", canonicalUrl);
      updateMetaTag("twitter:title", safeTitle);
      updateMetaTag("twitter:description", description);
      updateMetaTag("twitter:image", ogImageUrl);
      updateMetaTag("twitter:site", "@doodates");

      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);

      logger.info("SEO meta tags updated", "seo", {
        pollId: poll.id,
        pollType: poll.type,
        title: poll.title,
      });
    } catch (error) {
      logger.error("Failed to update SEO meta tags", "seo", {
        error,
        pollId: poll.id,
      });
    }
  }, [poll, baseUrl, i18n.language]);

  // This component doesn't render anything - it just manages meta tags
  return null;
};

// Utility function to detect category from title
export function detectPollCategory(
  title: string,
): "business" | "social" | "events" | "quizz" {
  const lowerTitle = title.toLowerCase();

  // Business keywords
  if (
    lowerTitle.match(
      // Mots anglais courts bornés (\b) : sans ça, « work » captait « Workshop »
      // et « pro » « programme »/« projection », classés business à tort.
      /réunion|meeting|\b(?:work|pro|team|call)\b|business|project|client|présentation|visio/,
    )
  ) {
    return "business";
  }

  // Social keywords
  if (
    lowerTitle.match(
      /soirée|sortie|restaurant|cinéma|weekend|vacances|amis|famille|anniversaire/,
    )
  ) {
    return "social";
  }

  // Events keywords
  if (
    lowerTitle.match(
      /événement|festival|concert|conférence|workshop|séminaire|formation|exposition/,
    )
  ) {
    return "events";
  }

  // Quizz keywords
  if (
    lowerTitle.match(
      /quiz|test|quizz|question|culture|connaissance|jeu|challenge/,
    )
  ) {
    return "quizz";
  }

  return "business"; // Default
}

// Utility function to format dates for display
export function formatDatesForOG(dates: string[]): string[] {
  return dates.slice(0, 3).map((dateStr) => {
    // `new Date("…")` ne lève jamais : une chaîne invalide donne un Date
    // invalide, que toLocaleDateString rend « Invalid Date ». On garde alors
    // la valeur d'origine.
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  });
}
